import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Mail, MessageSquare, HelpCircle, FileText } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const SupportPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('general');

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-gray-900">
      <SEOHead 
        title="Support & Help Center - AI BookMoney"
        description="Get support for AI BookMoney. Contact our team, browse FAQs, and find help with our AI-powered KDP publishing tools and services."
        keywords="AI BookMoney support, help center, customer service, publishing help, technical support"
        canonical="/support"
        ogTitle="Support & Help Center - AI BookMoney"
        ogDescription="Get support and help with AI BookMoney AI-powered publishing platform"
        ogImage="/ai-manuscript-demo.png"
        ogUrl="/support"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Support & Help Center",
          "description": "Get support for AI BookMoney platform",
          "url": "https://aibookmoney.com/support",
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "Customer Support",
            "email": "support@aibookmoney.com",
            "availableLanguage": "English"
          }
        }}
      />
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center text-purple-400 hover:text-purple-300 mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          
          <div className="flex items-center space-x-3 mb-4">
            <BookOpen className="h-8 w-8 text-purple-400" />
            <span className="text-2xl font-bold text-white">AI BookMoney</span>
          </div>
          
          <div className="flex items-center space-x-3">
            <HelpCircle className="h-8 w-8 text-blue-400" />
            <div>
              <h1 className="text-3xl font-bold text-white">Support Center</h1>
              <p className="text-gray-400 mt-1">Get help with AI BookMoney</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Quick Help Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Link 
            to="/kdp-guide"
            className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-lg p-6 hover:border-purple-400 transition-all group"
          >
            <FileText className="h-8 w-8 text-purple-400 mb-4 group-hover:text-purple-300" />
            <h3 className="text-lg font-semibold text-white mb-2">KDP Publishing Guide</h3>
            <p className="text-gray-400 text-sm">Learn how to use our tools and publish on Amazon KDP</p>
          </Link>
          
          <Link 
            to="/help"
            className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-lg p-6 hover:border-blue-400 transition-all group"
          >
            <HelpCircle className="h-8 w-8 text-blue-400 mb-4 group-hover:text-blue-300" />
            <h3 className="text-lg font-semibold text-white mb-2">Help Center</h3>
            <p className="text-gray-400 text-sm">Advanced tutorials and troubleshooting guides</p>
          </Link>
          
          <Link 
            to="/register"
            className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg p-6 hover:border-green-400 transition-all group"
          >
            <MessageSquare className="h-8 w-8 text-green-400 mb-4 group-hover:text-green-300" />
            <h3 className="text-lg font-semibold text-white mb-2">Start Free Trial</h3>
            <p className="text-gray-400 text-sm">Get 40 free credits to test all our tools</p>
          </Link>
        </div>

        {/* Contact Information */}
        <div className="bg-gray-800 rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <Mail className="h-6 w-6 text-purple-400 mr-3" />
            Contact Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">General Support</h3>
              <p className="text-gray-300 mb-2">For questions about our tools, billing, or account issues:</p>
              <a href="mailto:support@aibookmoney.com" className="text-purple-400 hover:text-purple-300 font-medium">
                support@aibookmoney.com
              </a>
              <p className="text-gray-400 text-sm mt-2">Response time: 24-48 hours</p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Technical Issues</h3>
              <p className="text-gray-300 mb-2">For bugs, API issues, or technical problems:</p>
              <a href="mailto:tech@aibookmoney.com" className="text-purple-400 hover:text-purple-300 font-medium">
                tech@aibookmoney.com
              </a>
              <p className="text-gray-400 text-sm mt-2">Response time: 12-24 hours</p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-gray-800 rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-lg font-semibold text-white mb-2">How does the credit system work?</h3>
              <p className="text-gray-300">
                Credits are consumed when you use our AI tools. Book generation costs 8-12 credits, covers cost 3-5 credits, 
                and metadata optimization costs 2-3 credits. Credits never expire as long as you have an active subscription.
              </p>
            </div>
            
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-lg font-semibold text-white mb-2">Can I cancel my subscription anytime?</h3>
              <p className="text-gray-300">
                Yes, you can cancel your subscription at any time through your account settings. Your access continues 
                until the end of your current billing period.
              </p>
            </div>
            
            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="text-lg font-semibold text-white mb-2">Do you provide refunds?</h3>
              <p className="text-gray-300">
                We offer a 14-day free trial so you can test our service before subscribing. Subscription fees and 
                credit purchases are generally non-refundable, but we'll work with you if there are technical issues.
              </p>
            </div>
            
            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="text-lg font-semibold text-white mb-2">Is the generated content copyright-safe?</h3>
              <p className="text-gray-300">
                Yes, all AI-generated content is original. However, we recommend reviewing and editing the content 
                before publishing to ensure it meets your standards and complies with platform requirements.
              </p>
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div className="bg-gray-800 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-white mb-6">Company Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Legal Entity</h3>
              <p className="text-gray-300 mb-2">JMFT LLC</p>
              <p className="text-gray-400 text-sm">AI BookMoney is operated by JMFT LLC</p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Business Hours</h3>
              <p className="text-gray-300 mb-1">Monday - Friday: 9:00 AM - 6:00 PM EST</p>
              <p className="text-gray-300">Weekend: Limited support via email</p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-3">Legal Documents</h3>
            <div className="flex flex-wrap gap-4">
              <Link to="/terms" className="text-purple-400 hover:text-purple-300 font-medium">
                Terms of Service
              </Link>
              <Link to="/privacy" className="text-purple-400 hover:text-purple-300 font-medium">
                Privacy Policy
              </Link>
              <a href="mailto:legal@aibookmoney.com" className="text-purple-400 hover:text-purple-300 font-medium">
                Legal Inquiries
              </a>
            </div>
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-semibold text-red-300 mb-2">Security Issues</h3>
          <p className="text-red-200 mb-3">
            If you discover a security vulnerability or believe your account has been compromised:
          </p>
          <a href="mailto:security@aibookmoney.com" className="text-red-300 hover:text-red-200 font-semibold">
            security@aibookmoney.com
          </a>
          <p className="text-red-200/80 text-sm mt-2">We take security issues seriously and will respond promptly.</p>
        </div>

      </div>
    </div>
  );
};

export default SupportPage;